/**
 * Dynamic content compiler types. A slot is an element in an email whose
 * content varies by audience branch; placement config lives in
 * content_dynamic_slot_usages (+ libraries/branches/contents) and the
 * element carries a `data-ht-dynamic-slot="<usageId>"` attribute in the
 * version HTML.
 *
 * The compiler turns (html, slots, target) into deployable artifacts: a
 * target-dialect template embedding every branch behind an if/elsif chain
 * over per-branch boolean fields, plus the structured assignment
 * (`SlotAssignment`) that decides each recipient's branch — consumed by SQL
 * rendering, the managed model's condition columns, and the deploy manifest,
 * so they always agree.
 */

import { z } from "zod";

import { ConditionType } from "../../../query/visual/types/condition";
import type { RootCondition } from "../../../query/visual/types/condition";
import { StringOperator } from "../../../query/visual/types/operator";
import { RESERVED_AID_VARIABLE_NAMES } from "../../../customer-data/decision-engine/reserved-aid-variable-names";
import {
  parseSlotBlocks,
  type SlotBlock,
} from "../../../content/dynamic-content/slot-content";

export type { AssignmentSource } from "./assignment-source";

export const DYNAMIC_SLOT_ATTRIBUTE = "data-ht-dynamic-slot";

/**
 * LLM / ad-hoc condition authors sometimes emit SQL-ish null checks
 * (`is_not_null` / `is_null`) that the visual query builder doesn't
 * understand — its null-check operators are `exists` / `notexists`. Without
 * this remap the Filter chip shows the empty "Filter on" placeholder and the
 * operator Select can't hold a value (so edits look stuck).
 */
const NULL_CHECK_OPERATOR_ALIASES: Record<string, string> = {
  is_not_null: StringOperator.Exists,
  is_null: StringOperator.DoesNotExist,
  "is not null": StringOperator.Exists,
  "is null": StringOperator.DoesNotExist,
};

/** Rewrite known invalid null-check operators into visual-query operators. */
export function normalizeAudienceConditionOperators<T>(condition: T): T {
  if (!condition || typeof condition !== "object") return condition;
  const walk = (node: unknown): unknown => {
    if (!node || typeof node !== "object") return node;
    if (Array.isArray(node)) return node.map(walk);
    const obj = node as Record<string, unknown>;
    const next: Record<string, unknown> = { ...obj };
    if (typeof next.operator === "string") {
      const mapped = NULL_CHECK_OPERATOR_ALIASES[next.operator];
      if (mapped) next.operator = mapped;
    }
    if (Array.isArray(next.conditions)) {
      next.conditions = next.conditions.map(walk);
    }
    if (Array.isArray(next.subconditions)) {
      next.subconditions = next.subconditions.map(walk);
    }
    return next;
  };
  return walk(condition) as T;
}

/** Flat field record for message-scoped SMS/push branches (spec: one slot
 * replaces the whole message; each branch carries the full field map). */
export type SlotBranchFields = Record<string, string>;

export type SlotBranch = {
  id: string;
  name: string;
  /** Audience condition AST for audience_schema assignment. Null for the
   * fallback branch, and for every branch when `assignment_source` is
   * `esp_profile`. */
  condition: RootCondition | null;
  /**
   * ESP-native bare predicate for `esp_profile` assignment (dialect depends on
   * the campaign ESP — e.g. Braze Liquid `custom_attribute.${tier} == 'gold'`).
   * Null/absent on the fallback and on audience_schema-mode branches.
   */
  espCondition?: string | null;
  /** Email element-scoped: the slot element's outerHTML (marker attribute
   * stripped) when this branch is assigned — rendering replaces the marked
   * element wholesale. Must be a single root element; empty means this
   * audience sees nothing. Empty when `fields` is set (message-scoped
   * SMS/push). */
  content: string;
  /** Message-scoped (SMS/push): full field record for this branch. */
  fields?: SlotBranchFields;
  /**
   * Fragment for a condition placement branch (`{ version: 1, blocks }` on
   * content_dynamic_slot_contents). Loose on purpose — the loader preserves
   * any product's vocabulary; LCM's compile re-parses strictly before use.
   */
  blocks?: SlotBlock[];
  /**
   * Usage-scoped content variant id (`content_dynamic_slot_contents.id`).
   * On load, set when an assignment exists. Preferred write: create via
   * `upsertContentEmailDynamicSlotContent`, then pass the same `contentId`
   * on sharing branches. Legacy writes omit it and mint 1:1 from `content`.
   */
  contentId?: string;
};

/** Fallback = no audience AST and no ESP predicate. */
export function isFallbackBranch(branch: {
  condition: unknown;
  espCondition?: string | null;
}): boolean {
  return (
    branch.condition == null &&
    (branch.espCondition == null || branch.espCondition === "")
  );
}

/**
 * Canvas/agent sentinel for a variant with content but no condition yet.
 * Distinct from the fallback (`condition: null`): fallback is the else arm,
 * unassigned ships to no one until the user fills it in.
 */
export const UNASSIGNED_AUDIENCE_CONDITION: RootCondition = {
  type: ConditionType.And,
  conditions: [],
};

/**
 * Unassigned = a variant with content that isn't attached to any condition:
 * no ESP predicate and an audience group with no rules — the
 * `{ type: "and", conditions: [] }` sentinel the editor saves for variants
 * left out of the conditions dialog. Deliberately distinct from the fallback
 * (condition null): the fallback ships as the else arm, an unassigned
 * variant ships to no one.
 */
export function isUnassignedBranch(branch: {
  condition: unknown;
  espCondition?: string | null;
}): boolean {
  if (branch.espCondition != null && branch.espCondition.trim() !== "") {
    return false;
  }
  const condition = branch.condition;
  return (
    typeof condition === "object" &&
    condition !== null &&
    "conditions" in condition &&
    Array.isArray(condition.conditions) &&
    condition.conditions.length === 0
  );
}

/** Library scope. `element` = HTML marker placement (email); `message` =
 * whole-message field record (SMS/push). */
export type DynamicSlotScope = "element" | "message";

export type DynamicSlotConfig = {
  id: string;
  branches: SlotBranch[];
  /** Library / slice display name — used for human-readable AID variable
   * names. Optional so compile fixtures and orphaned markers still work. */
  name?: string;
  /** Defaults to `element` for email slots. */
  scope?: DynamicSlotScope;
};

/** ESPs the compiler can emit templates for.
 * - `trigger_sync` / AID: iterable, braze, sfmc
 * - `esp_orchestrated`: those plus klaviyo (Django), sailthru (Zephyr) and
 *   xtremepush (Twig)
 * Native Delivery (CEP) is deliberately absent until its integration is
 * designed — it needs CEP-specific token/link handling, not just a dialect. */
const COMPILE_TARGET_LIST = [
  "iterable",
  "braze",
  "sfmc",
  "klaviyo",
  "sailthru",
  "xtremepush",
] as const;

export type CompileTarget = (typeof COMPILE_TARGET_LIST)[number];

/** Runtime companion to {@link CompileTarget}, for validating targets that
 * arrive as plain strings (GraphQL args, `destinations.type` rows). */
export const COMPILE_TARGETS: ReadonlySet<CompileTarget> = new Set(
  COMPILE_TARGET_LIST,
);

/** One case of a slot's waterfall: first matching condition wins. */
export type SlotAssignmentCase = {
  branchId: string;
  /** Boolean field for this branch, e.g. `ht_br_114b1654`, computed in the
   * warehouse by the deployment's managed model. Unused for `esp-profile`
   * (the template switches on {@link predicate} instead). */
  column: string;
  /** ESP-native predicate for `esp-profile` compile mode. */
  predicate?: string;
};

/**
 * The structured form of a slot's assignment logic. Everything downstream
 * (SQL preview, template switch, the managed model's condition columns)
 * derives from this one object. The template encodes the waterfall as an
 * if/elsif chain over the per-branch boolean columns, first match wins.
 */
export type SlotAssignment = {
  slotId: string;
  cases: SlotAssignmentCase[];
  fallbackBranchId: string;
};

export type CompileResult = {
  /** Target-dialect template with every branch embedded. */
  template: string;
  /** Subject rewritten into the target dialect when an input subject was
   * provided; otherwise null. SFMC subjects become AMPscript (including
   * Handlebars `{{#if}}` → compact `IIF` / `Concat`). */
  subject: string | null;
  assignments: SlotAssignment[];
  /** Per-recipient fields the send payload must supply: branch keys + merge
   * variables found in the template. */
  requiredFields: string[];
  /** Non-blocking issues (orphan slots/attributes). */
  warnings: string[];
  /** Blocking issues (incomplete conditions). Deploy must not proceed. */
  errors: string[];
};

/** SMS/push compile output: same assignment model as email, but a dialect
 * switch chain per named field instead of an HTML document. */
export type CompileMessageFieldsResult = {
  compiledFields: Record<string, string>;
  assignments: SlotAssignment[];
  requiredFields: string[];
  warnings: string[];
  errors: string[];
};

/** Column carrying a slot's winning branch id. Shared by CEP's condition_case
 * materialization and the ht_dynamic_slot tag that reads it.
 *
 * Keyed on the library, not the usage: conditions live on the library, so every
 * usage of it resolves the same branch and can share one column. */
export function slotColumnName(libraryId: string): string {
  return `ht_slot_${libraryId.replace(/-/g, "").slice(0, 8)}`;
}

/** Slug a display name into an AID-safe variable fragment (lowercase
 * alphanumerics + underscores). Empty / non-latin names return null so the
 * caller can fall back. */
export function slugifySlotVariableName(
  displayName: string | null | undefined,
): string | null {
  if (!displayName) return null;
  const slug = displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
  return slug || null;
}

/** Deterministic AID message-variable name for a slot. Shared by the
 * compiler (the template's `hightouch.recommendation.variables.<name>`
 * lookup) and the deploy resolver (the `decision_engine_message_variables`
 * row) so they always agree.
 *
 * Prefers a slug of the library/slice display name plus a short id suffix
 * for uniqueness when the same library is reused (`has_a_favorite_category_e57dbce3`).
 * Falls back to `slot_<id>` when no display name is available, or when the
 * slug would collide with {@link RESERVED_AID_VARIABLE_NAMES}. */
export function slotVariableName(
  slotId: string,
  displayName?: string | null,
): string {
  const suffix = slotId.replace(/-/g, "").slice(0, 8);
  const slug = slugifySlotVariableName(displayName);
  if (!slug || RESERVED_AID_VARIABLE_NAMES.has(slug)) {
    return `slot_${suffix}`;
  }
  return `${slug}_${suffix}`;
}

/**
 * How the deployment delivers sends — also selects the compile dialect:
 * - `trigger_sync`: warehouse `ht_br_*` booleans + managed sync
 * - `ai_decisioning`: AID bandit chooses a branch per slot
 * - `esp_orchestrated`: ESP-native predicates on profile attributes (Braze
 *   Liquid first). No warehouse booleans and no `api_trigger_properties` rewrite.
 */
export type ContentDeploymentDeliveryType =
  | "trigger_sync"
  | "ai_decisioning"
  | "esp_orchestrated";

const CONDITION_TYPES = new Set<string>(Object.values(ConditionType));

/** Shallow shape check for an audience condition AST: an object whose `type`
 * is a known ConditionType. Deep validation is left to the visual-query
 * compiler, which rejects malformed nodes at translate time. */
export function isRootConditionShape(value: unknown): value is RootCondition {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    typeof value.type === "string" &&
    CONDITION_TYPES.has(value.type)
  );
}

/** Absent (null/undefined) condition = the fallback branch. Present values
 * get the shallow shape check plus operator-alias normalization. */
const slotConditionSchema = z
  .custom<RootCondition>(isRootConditionShape)
  .nullish()
  .transform((value) =>
    value == null ? null : normalizeAudienceConditionOperators(value),
  );

const espConditionSchema = z
  .string()
  .nullish()
  .transform((value) => {
    if (value == null) return null;
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  });

const slotBranchSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    condition: slotConditionSchema,
    espCondition: espConditionSchema,
    content: z.unknown(),
    fields: z.record(z.string()).nullish(),
    blocks: z.array(z.unknown()).nullish(),
    contentId: z.string().uuid().nullish(),
  })
  .transform((branch, ctx): SlotBranch => {
    const fields = branch.fields ?? undefined;
    const blocks =
      branch.blocks == null
        ? undefined
        : (parseSlotBlocks(branch.blocks) ?? undefined);
    if (branch.blocks != null && blocks === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Branch blocks must be objects with a string type.",
      });
      return z.NEVER;
    }
    // `content` must be a string, except message-scoped (fields/blocks) and
    // create-then-reference writes (`contentId` only) may omit it.
    const contentId = branch.contentId ?? undefined;
    const content =
      typeof branch.content === "string"
        ? branch.content
        : fields || blocks || contentId
          ? ""
          : null;
    if (content === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Branch has neither string content, fields, blocks, nor contentId.",
      });
      return z.NEVER;
    }
    if (branch.condition != null && branch.espCondition != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Branch cannot carry both an audience condition and an ESP predicate.",
      });
      return z.NEVER;
    }
    return {
      id: branch.id,
      name: branch.name,
      condition: branch.condition,
      ...(branch.espCondition != null
        ? { espCondition: branch.espCondition }
        : {}),
      content,
      ...(fields ? { fields } : {}),
      ...(blocks ? { blocks } : {}),
      ...(contentId ? { contentId } : {}),
    };
  });

const slotRowSchema = z.object({
  id: z.string().min(1),
  branches: z.array(slotBranchSchema),
  scope: z.enum(["element", "message"]).nullish(),
});

/**
 * Narrow a dynamic-slot row's branches jsonb into a DynamicSlotConfig.
 * Returns null for malformed rows. Shape-only: product invariants (e.g.
 * every slot needs a fallback branch) are enforced by the compiler, which
 * reports them as blocking errors instead of silently dropping the slot.
 */
export function parseSlotRow(row: {
  id: string;
  branches: unknown;
  scope?: unknown;
}): DynamicSlotConfig | null {
  const parsed = slotRowSchema.safeParse(row);
  if (!parsed.success) return null;
  const { id, branches, scope } = parsed.data;
  return { id, branches, ...(scope ? { scope } : {}) };
}
